import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Router } from "@angular/router";
import { Contract } from "@edp-components/edp-contract";
import { ExternalLinkService } from "@edp-directives/external-link/external-link.service";
import { UserNotification } from "@edp-models/user-notification.model";
import { SelectContract } from "@edp-store/actions/contract.actions";
import { SelectOverlayAction, SetTabMenu } from "@edp-store/actions/layout.actions";
import { UserNotificationsMarkReaded, UserNotificationsMarkViewed } from "@edp-store/actions/user.actions";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { getActiveTabMenu } from "@edp-store/selectors/layout.selectors";
import { getUserNotificationsList } from "@edp-store/selectors/user.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { GoogleAnalyticsService } from "@edp-services/google-analytics.service";
import { OperationHistoryService } from "@edp-services/operation-history.service";

@Component({
    selector: "edp-overlay-notifications",
    templateUrl: "./edp-overlay-notifications.component.html",
    styleUrls: ["./edp-overlay-notifications.component.scss"],
})
export class EdpOverlayNotificationsComponent implements OnInit, OnDestroy {
    @Input()
    public overlayChagesListner: Subject<{ type: string; event: "hide" | "show" }>;
    @Output()
    public closeSideBar = new EventEmitter<boolean>();

    public notificationsList: UserNotification[];
    public fullNotificationsList: UserNotification[];
    public contract: Contract;
    public tabSelected = "ALL";
    private unsubscribe = new Subject();
    private activeFilter$: Observable<string>;
    public isLoaded = false;

    private OPERATION_ID_CLICK      = "FSM_PUSH_CLICK";
    private OPERATION_ID_RECEPTION  = "FSM_PUSH_RECEPTION";

    constructor(
        private store: Store<IAppState>,
        private router: Router,
        private readonly externalLinkService: ExternalLinkService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private operationHistoryService: OperationHistoryService
        ) {
        this.activeFilter$ = this.store.pipe(select(getActiveTabMenu));
    }

    public ngOnInit(): void {
        this.store.pipe(
            select(getContract),
            takeUntil(this.unsubscribe),
            filter(contract => contract !== null && contract !== undefined)
        ).subscribe(contract => {
            this.contract = contract;
        });

        this.store
            .pipe(
                takeUntil(this.unsubscribe),
                select(getUserNotificationsList),
                filter((val: UserNotification[]) => !!val)
            )
            .subscribe((val: UserNotification[]) => {
                this.isLoaded = true;
                this.fullNotificationsList = [...val];
                this.validateList();
            });

        if (this.overlayChagesListner) {
            this.overlayChagesListner
                .pipe(takeUntil(this.unsubscribe), ofType("notifications"))
                .subscribe((e: { type: string; event: "hide" | "show" }) => {
                    if (e.event === "show") {
                        this.hopProcess();
                        this.store.dispatch(new UserNotificationsMarkViewed());
                    }
                });
        }

        this.activeFilter$.pipe(takeUntil(this.unsubscribe)).subscribe(filter => {
            this.tabSelected = filter;
            this.validateList();
        });
    }

    public close() {
        this.closeSideBar.emit();
    }

    public markReaded(notification: UserNotification) {
        if (!notification.isReaded()) {
            this.store.dispatch(new UserNotificationsMarkReaded(notification));
        }
        this.redirect(notification);
    }

    public redirect(notification: UserNotification) {
        const url = notification.Url;
        const data = [];
        let contractId: string;
        url.split("#").forEach(part => {
            if (part) {
                const splits = part.split("=");
                data.push(splits[1]);
            }
        });

        const goTo = data[0];
        if (data.length > 1) {
            contractId = data[1];
        }


        if (contractId) {
            this.store.dispatch(new SelectContract(notification.Contract));
        }
        switch (goTo) {
            case "NEW_PLAN":
                this.router.navigate(["/novo-plano"]);
                break;
            case "CONTRACT_LIST":
                this.router.navigate(["/energia/inicio"]);
                break;
            case "VANTAGENS":
                this.router.navigate([`/energia/${contractId}/leisure`]);
                break;
            case "ADD_CONTACERTA":
                this.router.navigate([`/locais-consumo/${contractId}/pagamento/alteracao`]);
                break;
            case "ENVIO_LEITURAS":
                this.router.navigate([`/energia/${contractId}/inicio`]);
                break;
            case "DASHBOARD_Z":
                this.router.navigate([`/planeta-zero`]);
                break;
            case "FSM":
                this.operationHistoryService.registerWithContract(notification.ContractId, this.OPERATION_ID_CLICK, notification.Url);
                let externalLink: string;
                const messageLinkRegex = new RegExp(/link:\s*((http|https):[^\s]+)/);
                const matches = messageLinkRegex.exec(notification.Message);
                if (matches?.length >= 2) {
                    externalLink = matches[1];
                }
                this.router.navigate([`/pedidos-apoio`]).then((isOpen) => {
                    if (isOpen && Boolean(externalLink)) {
                        this.externalLinkService.externalLink(externalLink);
                    }
                });
                break;
            default:
                this.router.navigate([`/${goTo}`]);
                break;
        }
        this.close();
    }

    public onTabChange(tabSelection) {
        this.store.dispatch(new SetTabMenu(tabSelection));
    }

    private validateList() {
        switch (this.tabSelected) {
            case "ALL":
                this.notificationsList = this.fullNotificationsList;
                break;
            default:
                this.notificationsList = this.fullNotificationsList.filter(
                    notification => notification.ContractId === this.contract?.ContractId
                );
                break;
        }
    }

    private hopProcess() {
        this.fullNotificationsList.forEach(notif => {
            if (notif.Type === "FSM" && !notif.isViewed()) {
                this.operationHistoryService.registerWithContract(notif.ContractId, this.OPERATION_ID_RECEPTION);
            }
        });
    }

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public openPushConfigs() {
        this.close();

        this.store.dispatch(new SelectOverlayAction("push-configs"));
    }
}
